import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { FileInfo, GalaxyUploaderService, UploadContext, UploadResponse } from '@vendasta/galaxy/uploader';
import { CreateFileRequest, DeleteFileRequest, FileAccessType, FilesApiService } from '@vendasta/media';
import {
  catchError,
  combineLatest,
  concat,
  EMPTY,
  ignoreElements,
  map,
  Observable,
  of,
  ReplaySubject,
  throwError,
} from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SourceId } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN } from '../../../../../../core/src/index';
import { MAX_FILES } from '../../../../../../core/src/lib/conversation.constants';

const VIDEO_MIME_TYPES = ['video/mp4', 'video/3gpp', 'video/3gp'];
const CSV_MIME_TYPE = 'text/csv';
const SPREADSHEET_MIME_TYPE = 'application/vnd.ms-excel';
const CSV_MIME_TYPES = [CSV_MIME_TYPE, SPREADSHEET_MIME_TYPE];
const AI_ASSISTANT_CSV_MAX_BYTES = 10 * 1024 * 1024;
const MEDIA_FILES_PATH_PREFIX = '/files/v3/';
const MAX_FILE_SIZE_EXCEEDED = 'GALAXY.UPLOADER.ERROR.MAX_FILE_SIZE_EXCEEDED';

export const AiAssistantUploadErrorKey = {
  fileTypeNotSupported: 'INBOX.UPLOAD_FILE.AI_ASSISTANT.FILE_TYPE_NOT_SUPPORTED',
  csvTooLarge: 'INBOX.UPLOAD_FILE.AI_ASSISTANT.CSV_TOO_LARGE',
  csvUploadFailed: 'INBOX.UPLOAD_FILE.AI_ASSISTANT.CSV_UPLOAD_FAILED',
} as const;

interface CreatedMediaFile {
  namespaceId: string;
  signedUrl: string;
  fileUrl: string;
}

import { isMockVideoEnabled } from '../mock-video-panel/mock-video-utils';

@Injectable()
export class InboxUploadService extends GalaxyUploaderService {
  conversationId$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  conversationId$: Observable<string> = this.conversationId$$.asObservable();
  chatSourceId: SourceId | undefined;

  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly filesApiService = inject(FilesApiService);

  constructor(
    http: HttpClient,
    private readonly env: EnvironmentService,
  ) {
    super(http);
  }

  addFile(file: File, context?: UploadContext): void {
    const error = getAiAssistantAddFileError(this.chatSourceId, file);
    if (error) {
      this.fileErrored$$.next({ fileInfo: { file, name: file.name, context }, error: new Error(error) });
      return;
    }
    super.addFile(file, context);
  }

  buildRequest(fileInfo: FileInfo): Observable<UploadResponse> {
    // TODO(TIF-21 Phase 1): Remove mockVideo query param and mock paths — mock intercept for video transcoding pipeline
    if (isMockVideoEnabled() && fileInfo.file && VIDEO_MIME_TYPES.includes(fileInfo.file.type)) {
      return of({
        data: { url: 'mock://video' },
      });
    }

    const file = fileInfo.file;
    const isSpreadsheet = !!file && (isCsvFile(file) || file.type === SPREADSHEET_MIME_TYPE);
    if (this.chatSourceId === 'ai-assistant' && file && isSpreadsheet) {
      return isCsvFile(file)
        ? this.uploadCsvToMedia(file)
        : throwError(() => new Error('only CSV spreadsheets can be sent to an AI assistant'));
    }
    return this.uploadToConversation(fileInfo);
  }

  private uploadCsvToMedia(file: File): Observable<UploadResponse> {
    if (file.size > AI_ASSISTANT_CSV_MAX_BYTES) {
      return throwError(() => new Error('CSV exceeds the AI assistant size limit'));
    }
    return this.hostAppInterface.getNamespace().pipe(
      take(1),
      switchMap((namespace) => this.createCsvFile(namespace.id, file)),
      switchMap((created) => this.putCsv(file, created)),
    );
  }

  private createCsvFile(namespaceId: string, file: File): Observable<CreatedMediaFile> {
    const request = new CreateFileRequest({
      namespace: namespaceId,
      fileMetadata: {
        fileName: file.name,
        path: 'conversation/ai-assistant',
        contentType: CSV_MIME_TYPE,
        accessType: FileAccessType.PRIVATE,
      },
    });
    return this.filesApiService.createFile(request).pipe(
      map(({ signedUrl, fileUrl }) => {
        if (!signedUrl || !fileUrl) {
          throw new Error('media returned no signed upload URL');
        }
        return { namespaceId, signedUrl, fileUrl };
      }),
    );
  }

  private putCsv(file: File, { namespaceId, signedUrl, fileUrl }: CreatedMediaFile): Observable<UploadResponse> {
    return this.http.put(signedUrl, file, { headers: { 'Content-Type': CSV_MIME_TYPE }, withCredentials: false }).pipe(
      map(() => csvUploadResponse(file, fileUrl)),
      catchError((err) =>
        concat(
          this.deleteMediaFile(namespaceId, fileUrl),
          throwError(() => err),
        ),
      ),
    );
  }

  private deleteMediaFile(namespace: string, fileUrl: string): Observable<never> {
    return this.filesApiService.deleteFile(new DeleteFileRequest({ namespace, fileId: { fileUrl } })).pipe(
      ignoreElements(),
      catchError(() => EMPTY),
    );
  }

  private uploadToConversation(fileInfo: FileInfo): Observable<UploadResponse> {
    this.uploadUrl = 'https://conversation-demo.apigateway.co/upload-file';
    if (this.env.getEnvironment() === Environment.PROD) {
      this.uploadUrl = 'https://conversation-prod.apigateway.co/upload-file';
    }
    return combineLatest([this.conversationId$, this.hostAppInterface.getNamespace()]).pipe(
      take(1),
      switchMap(([conversationId, namespace]) => {
        const params = new HttpParams().set('conversationID', conversationId || '').set('namespaceID', namespace.id);
        const body = new FormData();
        body.append('file', fileInfo.file as Blob);
        return this.http.post(this.uploadUrl!, body, { withCredentials: true, params }) as Observable<UploadResponse>;
      }),
    );
  }
}

function isCsvFile(file: File): boolean {
  return file.type === CSV_MIME_TYPE || file.name.toLowerCase().endsWith('.csv');
}

/** Returns the error a file gets before it is queued on the AI-assistant channel, or undefined to queue it. */
export function getAiAssistantAddFileError(chatSourceId: SourceId | undefined, file: File): string | undefined {
  if (chatSourceId !== 'ai-assistant') {
    return undefined;
  }
  if (file.type === SPREADSHEET_MIME_TYPE && !isCsvFile(file)) {
    return AiAssistantUploadErrorKey.fileTypeNotSupported;
  }
  if (isCsvFile(file) && file.size > AI_ASSISTANT_CSV_MAX_BYTES) {
    return MAX_FILE_SIZE_EXCEEDED;
  }
  return undefined;
}

/** Returns the AI-assistant copy for an upload error on a spreadsheet, or undefined when the generic copy applies. */
export function getAiAssistantCsvError(
  chatSourceId: SourceId | undefined,
  file: File | undefined,
  message: string,
): { key: string; maxFileSize?: string } | undefined {
  if (chatSourceId !== 'ai-assistant' || !file) {
    return undefined;
  }
  if (!isCsvFile(file)) {
    return file.type === SPREADSHEET_MIME_TYPE ? { key: AiAssistantUploadErrorKey.fileTypeNotSupported } : undefined;
  }
  switch (message) {
    case MAX_FILE_SIZE_EXCEEDED:
      return { key: AiAssistantUploadErrorKey.csvTooLarge, maxFileSize: formatBytes(AI_ASSISTANT_CSV_MAX_BYTES) };
    case 'GALAXY.UPLOADER.ERROR.GENERIC':
      return { key: AiAssistantUploadErrorKey.csvUploadFailed };
    default:
      return undefined;
  }
}

export function getFileTypeNotSupportedMessage(chatSourceId?: SourceId): string {
  return chatSourceId === 'ai-assistant'
    ? AiAssistantUploadErrorKey.fileTypeNotSupported
    : 'GALAXY.UPLOADER.ERROR.FILE_TYPE_NOT_SUPPORTED';
}

function csvUploadResponse(file: File, fileUrl: string): UploadResponse {
  return {
    data: {
      url: fileUrl,
      media_url: fileUrl,
      media_file_name: file.name,
      media_content_type: CSV_MIME_TYPE,
      media_location_path: new URL(fileUrl).pathname.slice(MEDIA_FILES_PATH_PREFIX.length),
      media_file_size: file.size,
    },
  } as UploadResponse;
}

/**
 * Returns the max file size in bytes for a channel, optionally narrowed by file type.
 * @param chatSourceId
 * @param fileType
 */
export function getMaxFileSize(chatSourceId?: SourceId, fileType?: string): number {
  if (!chatSourceId) {
    return 0;
  }
  switch (chatSourceId) {
    case 'ai-assistant':
      return fileType && CSV_MIME_TYPES.includes(fileType) ? AI_ASSISTANT_CSV_MAX_BYTES : 250 * 1024 * 1024;
    case 'sms':
      if (fileType === 'application/pdf') {
        return 600 * 1024; // Twilio non-image MMS recommendation
      }
      if (
        fileType === 'image/jpeg' ||
        fileType === 'image/jpg' ||
        fileType === 'image/png'
      ) {
        return 20 * 1024 * 1024;
      }
      return 250 * 1024 * 1024; // videos transcoded server-side
    case 'email':
      return 10 * 1024 * 1024; // 10MB
    default:
      return 250 * 1024 * 1024; // 250MB
  }
}

/**
 * Per-MIME-type max file size overrides for a channel, if applicable.
 * @param chatSourceId
 */
export function getMaxFileSizeByType(chatSourceId?: SourceId): Record<string, number> | undefined {
  if (chatSourceId === 'sms') {
    const size = (type: string) => getMaxFileSize(chatSourceId, type);
    return {
      'application/pdf': size('application/pdf'),
      'image/jpeg': size('image/jpeg'),
      'image/jpg': size('image/jpg'),
      'image/png': size('image/png'),
    };
  }
  if (chatSourceId === 'ai-assistant') {
    return Object.fromEntries(CSV_MIME_TYPES.map((type) => [type, AI_ASSISTANT_CSV_MAX_BYTES]));
  }
  return undefined;
}

/** Shared so the composer's menu entry and the paperclip cannot disagree on who may upload. */
export function canUploadToSource(chatSourceId?: SourceId): boolean {
  return chatSourceId !== 'google';
}

/**
 * Per-MIME-type max file count overrides for a channel, if applicable.
 * @param chatSourceId
 */
export function getMaxFilesByType(chatSourceId?: SourceId): Record<string, number> | undefined {
  if (chatSourceId === 'sms') {
    // single PDF attachment per message
    return { 'application/pdf': MAX_FILES };
  }
  return undefined;
}

/**
 * Returns the accepted file types for a given channel
 * @param chatSourceId
 */
export function getAcceptFileTypes(chatSourceId?: SourceId): string {
  if (!chatSourceId) {
    return '';
  }
  switch (chatSourceId) {
    case 'sms':
      return 'image/jpeg, image/jpg, image/png, image/gif, application/pdf, video/mp4, video/quicktime, video/3gpp';
    case 'whatsapp':
      return [
        'image/jpeg',
        'image/png',
        'video/3gp',
        'video/mp4',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/pdf',
      ].join(', ');
    case 'openai':
      return 'image/jpeg, image/jpg, image/png, image/gif, image/webp';
    case 'ai-assistant':
      return 'image/jpeg, image/jpg, image/png, image/gif, image/webp, text/csv, .csv, application/vnd.ms-excel';
    default:
      return '*/*';
  }
}

function formatBytes(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) {
    return `${Number.isInteger(mb) ? mb : Number(mb.toFixed(2))} MB`;
  }
  const kb = bytes / 1024;
  return `${Number.isInteger(kb) ? kb : Number(kb.toFixed(2))} KB`;
}

export function getMaxFileSizeErrorDisplay(chatSourceId?: SourceId, fileType?: string): string {
  const bytes = getMaxFileSize(chatSourceId, fileType);
  if (!bytes) {
    return '';
  }
  const display = formatBytes(bytes);
  if (
    chatSourceId === 'sms' &&
    fileType === 'image/gif'
  ) {
    return `${display} (GIF limit). ` +
      'JPEG and PNG support up to 20 MB';
  }
  return display;
}
